import {
  Button,
  Card,
  CardContent,
  CardHeader,
  FormLabel,
  Stack,
  Typography,
} from '@mui/material';
import { FieldTextRequired } from '../../form';
import { useForm } from 'react-typed-form';
import { useTypedDispatch } from '../../reducers';
import { useApiCallable } from '../../api';
import { ApiError } from '../../api/sustain-api-request';
import { ErrorList } from '../../components';

type Props = {
  email: string;
  token: string;
};

type SetPasswordForm = {
  newPassword: string;
  confirmPassword: string;
};

export default function SetPasswordForm({ email, token }: Props) {
  const dispatch = useTypedDispatch();
  const callApi = useApiCallable();

  const form = useForm<SetPasswordForm>({
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
    onSubmit: async (values, { setLoading }) => {
      setLoading(true);

      if (values.newPassword !== values.confirmPassword) {
        form.addSubmitError('_form', 'Passwords do not match');
        setLoading(false);
        return;
      }

      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const setPasswordResponse = await callApi<any, any>(
        '/set-password',
        {
          method: 'POST',
          jsonBody: {
            timezone,
            email,
            token,
            newPassword: values.newPassword,
          },
        },
        {
          errorHandler: (error, originalHandler) => {
            if (error instanceof ApiError && error.response.status === 400) {
              form.addSubmitError('_form', error.response.data.error);
            }
            originalHandler(error);
          },
        },
      );

      if (!setPasswordResponse) {
        setLoading(false);
        return;
      }

      const loginResponse = await callApi<
        {
          twoFactorRequired: boolean;
          sms2faMetadata: { phoneLastDigits: string } | null;
          user: {
            id: string;
            email: string;
            role?: string[];
            timezone?: string;
          } | null;
        },
        any
      >(
        '/login-check',
        {
          method: 'POST',
          jsonBody: {
            email,
            password: values.newPassword,
          },
        },
        {
          errorHandler: (error, originalHandler) => {
            if (error instanceof ApiError && error.response.status === 401) {
              form.addSubmitError('_form', error.response.data.error);
            }
            originalHandler(error);
          },
        },
      );

      setLoading(false);

      if (!loginResponse) return;

      const sessionId = (loginResponse.headers as any).get(
        'sustain-session-id',
      );

      if (!sessionId) {
        alert('No session id');
        return;
      }

      const { twoFactorRequired, user } = loginResponse.data;

      if (twoFactorRequired) {
        dispatch({
          type: 'LOGIN_AWAITING_2FA',
          payload: {
            sessionId,
          },
        });
      } else if (user) {
        dispatch({
          type: 'LOGIN',
          payload: {
            user: {
              id: user.id,
              email: user.email,
              sessionId,
              roles: user.role ?? [],
              timezone: user.timezone ?? timezone,
            },
          },
        });
      }
    },
  });

  return (
    <form
      onSubmit={(ev) => {
        ev.preventDefault();
        form.handleSubmit();
      }}
    >
      <Card>
        <CardHeader title="Set your password" />
        <CardContent>
          <Stack spacing={2}>
            <Stack spacing={1}>
              <FormLabel>Email address</FormLabel>
              <Typography>{email}</Typography>
            </Stack>
            <FieldTextRequired
              label="Password"
              field={form.getField('newPassword')}
              type="password"
            />
            <FieldTextRequired
              label="Confirm Password"
              field={form.getField('confirmPassword')}
              type="password"
            />
            <Button variant="contained" type="submit" disabled={form.isLoading}>
              Submit
            </Button>
            <ErrorList errors={form.lastErrors._form} />
          </Stack>
        </CardContent>
      </Card>
    </form>
  );
}
